import { create } from "zustand";
import { authenticatedAxios, baseURL } from "../../apis";

const groupProfilesByBot = (profiles) => {
  if (!Array.isArray(profiles)) return [];

  const grouped = profiles.reduce((acc, profile) => {
    const botId = profile.bot_master_id;
    if (!acc[botId]) {
      acc[botId] = {
        bot_master_id: profile.bot_master_id,
        bot_master_bot_name: profile.bot_master_bot_name,
        is_active: profile.is_active !== undefined ? profile.is_active : true,
        parameters_level_grouped: {},
      };
    }
    acc[botId].parameters_level_grouped[profile.parameter_level] =
      profile.parameters;
    return acc;
  }, {});
  return Object.values(grouped);
};

const useBotStrategyStore = create((set, get) => ({
  strategies: [],
  botCounts: {},
  coins: [],
  botMasters: [],
  isLoading: false,
  isCountLoading: false,
  isSubmitting: false,
  isCoinLoading: false,
  error: null,

  //  Get all bot profiles grouped by master
  getBotStrategies: async () => {
    set({ isLoading: true });
    try {
      const { data } = await authenticatedAxios.get(
        `${baseURL}/api/admin/bot_profiles/`
      );
      const groupedData = groupProfilesByBot(data);
      set({ strategies: groupedData, isLoading: false });
    } catch (err) {
      console.error("Failed to fetch bot profiles:", err);
      set({ error: err, isLoading: false });
    }
  },

  // ✅ Fetch bot profile detail (single bot or grouped by level)
  getBotProfileDetail: async (botMasterId, parameterLevel = null) => {
    set({ isDetailLoading: true, botDetail: null });
    try {
      const url = new URL(`${baseURL}/api/admin/bot_profile/`);
      url.searchParams.append("bot_master_id", botMasterId);
      if (parameterLevel)
        url.searchParams.append("parameter_level", parameterLevel);

      const { data } = await authenticatedAxios.get(url.toString());
      console.log("data", data);

      set({ botDetail: data, isDetailLoading: false });
      return data;
    } catch (err) {
      console.error("❌ Failed to fetch bot profile detail:", err);
      set({ error: err, isDetailLoading: false });
      return null;
    }
  },

  // ✅ Update grouped bot strategy (multiple parameter levels)
  updateBotStrategy: async (profileGroupId, payload) => {
    set({ isSubmitting: true, error: null });
    try {
      // Ensure parameters are properly formatted
      // const formattedPayload = {
      //   bot_master_id: Number(payload.bot_master_id),
      //   parameter_level: payload.parameter_level,
      //   parameters: Object.entries(payload.parameters || {}).map(
      //     ([key, val]) => ({
      //       parameter_key: key,
      //       parameter_value: val ?? null,
      //     })
      //   ),
      //   name: payload.name,
      //   description: payload.description,
      //   coin_id: Number(payload.coin_id),
      // };

      const { data } = await authenticatedAxios.put(
        `${baseURL}/api/admin/bot_profile/${profileGroupId}/`,
        payload
      );

      // Refresh after update
      await get().getBotStrategies();

      set({ isSubmitting: false });
      return data;
    } catch (err) {
      console.error("❌ Failed to update bot strategy:", err);
      set({ error: err, isSubmitting: false });
      return null;
    }
  },

  deleteBotProfile: async (profileGroupId) => {
    set({ isSubmitting: true });
    try {
      await authenticatedAxios.delete(
        `${baseURL}/api/admin/bot_profile/${profileGroupId}/delete/`
      );

      // Refresh after delete
      await get().getBotStrategies();
      set({ isSubmitting: false });
      return true;
    } catch (err) {
      console.error("❌ Failed to delete bot profile:", err);
      set({ error: err, isSubmitting: false });
      return false;
    }
  },

  // ✅ Create new bot profile
  createBotProfile: async (payload) => {
    set({ isSubmitting: true, error: null });
    try {
      const { data } = await authenticatedAxios.post(
        `${baseURL}/api/admin/bot_profile/create`,
        payload
      );

      // Refresh strategies after creation
      await get().getBotStrategies();

      set({ isSubmitting: false });
      return data;
    } catch (err) {
      console.error("Failed to create bot profile:", err);
      set({ error: err, isSubmitting: false });
      return null;
    }
  },

  // ✅ Get bot count summary
  getBotCounts: async () => {
    set({ isCountLoading: true });
    try {
      const { data } = await authenticatedAxios.get(
        `${baseURL}/api/admin/bots/count`
      );
      set({ botCounts: data, isCountLoading: false });
    } catch (err) {
      console.error("Failed to fetch bot counts:", err);
      set({ error: err, isCountLoading: false });
    }
  },

  getBotMasters: async () => {
    set({ isLoading: true });
    try {
      const { data } = await authenticatedAxios.get(
        `${baseURL}/api/admin/bot_master/`
      );
      set({ botMasters: data, isLoading: false });
    } catch (err) {
      console.error("Failed to fetch bot masters:", err);
      set({ isLoading: false });
    }
  },

  toggleBotProfileStatus: async (profileGroupId) => {
    set({ isSubmitting: true });
    try {
      const { data } = await authenticatedAxios.put(
        `${baseURL}/api/admin/bot_profile/${profileGroupId}/toggle-status/`
      );

      // ✅ Refresh main bot list after toggling
      await get().getBotStrategies();

      set({ isSubmitting: false });
      console.log("✅ Toggle success:", data.message);
      return true;
    } catch (err) {
      console.error("❌ Failed to toggle profile status:", err);
      set({ error: err, isSubmitting: false });
      return false;
    }
  },
}));

export default useBotStrategyStore;
